import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Card } from '@parrot-software/design-system';
import { ExternalLink, ReceiptText } from '@parrot-software/design-system/icons';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { AdvanceInvoiceBadge } from '@subscriptions/presentation/components/base';
import { useFiscalRegimes } from '@subscriptions/presentation/hooks';
import {
  getInvoiceDisplayName,
  getInvoicingDeadlineLabel,
  getTaxRegimeLabel,
  hasCompleteInvoiceData,
} from '@subscriptions/presentation/utils';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';

interface BlockInvoiceDataCardProps {
  block: SubscriptionBlockViewDto;
}

const Field = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-px">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

/**
 * Fiscal record of the block, with a link into the invoicing tab to review or complete it. A read-only
 * organization keeps the link as a disabled button, since that tab is locked for it.
 */
export const BlockInvoiceDataCard = ({ block }: BlockInvoiceDataCardProps) => {
  const { t } = useTranslation('subscriptions');
  const { data: fiscalRegimes } = useFiscalRegimes();
  const isComplete = hasCompleteInvoiceData(block.invoiceData);
  const invoicingPath = `/subscriptions/blocks/${block.uuid}?tab=invoicing`;
  const areActionsLocked = useAreSubscriptionActionsLocked();
  const linkLabel = isComplete
    ? t('blockDetail.summary.invoiceDataCard.view')
    : t('blockDetail.summary.invoiceDataCard.complete');

  return (
    <Card className="p-5 font-sans shadow-xs">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <ReceiptText className="h-4 w-4" />
        {t('blockDetail.summary.invoiceDataCard.title')}
      </div>

      {isComplete && block.invoiceData ? (
        <div className="mt-2.5 flex flex-col gap-2 text-sm">
          <Field
            label={t('blockDetail.summary.invoiceDataCard.businessName')}
            value={getInvoiceDisplayName(block.invoiceData)}
          />
          <Field label={t('blockDetail.summary.invoiceDataCard.taxId')} value={block.invoiceData.rfc} />
          <Field
            label={t('blockDetail.summary.invoiceDataCard.taxRegime')}
            value={getTaxRegimeLabel(block.invoiceData.taxRegime, fiscalRegimes)}
          />

          <p className="text-xs leading-relaxed text-muted-foreground">
            {t('blockDetail.summary.invoiceDataCard.cfdiNote', { deadline: getInvoicingDeadlineLabel() })}
          </p>
        </div>
      ) : (
        <>
          <p className="mt-2.5 text-sm font-medium text-destructive">
            {t('blockDetail.summary.invoiceDataCard.missing')}
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            {t('blockDetail.summary.invoiceDataCard.missingHint')}
          </p>
        </>
      )}

      {block.advanceInvoice && (
        <div className="mt-3 flex items-center gap-2">
          <AdvanceInvoiceBadge />
          <p className="text-xs text-muted-foreground">{t('blockDetail.summary.advanceInvoiceHint')}</p>
        </div>
      )}

      {areActionsLocked ? (
        <Button className="mt-2.5 h-8 px-0" disabled size="sm" variant="link">
          <ExternalLink className="mr-1 h-3.5 w-3.5" />
          {linkLabel}
        </Button>
      ) : (
        <Button asChild className="mt-2.5 h-8 px-0" size="sm" variant="link">
          <Link to={invoicingPath}>
            <ExternalLink className="mr-1 h-3.5 w-3.5" />
            {linkLabel}
          </Link>
        </Button>
      )}
    </Card>
  );
};
